import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfigService, buildParameters, Page } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { map, Observable, switchMap } from 'rxjs';
import { FurtherQualificationLevel3RequestDto } from '@markant-platform/shared/organization/model';
import { SortAttributes } from '@markant-platform/shared/table/model';
import { Operation } from '@markant-platform/shared/util-helpers';
import {
  Agent,
  BackofficeVerificationDto,
  CompleteRegistrationRequestDto,
  ExistingCustomerRegistrationDto,
  OrganizationAndUserRegistrationAuditDto,
  OrganizationAndUserRegistrationDto,
  PlatformRequestData,
  PlatformRequestOrganization,
  PlatformRequestsDto,
  PlatformRequestStatus,
  PlatformRequestUserUpdateDto,
  RegistrationDetailsResponseDto,
  UserContactDto,
} from '@markant-platform/registration/model';
import {
  CommentRequestDto,
  DuplicateResponseDto,
  GlnValidationResponseDto,
  PartnerPoolDataResult,
  VerifyEmailResult,
} from '@markant-platform/platform-requests/model';

@Injectable({
  providedIn: 'root',
})
export class PlatformRequestService {
  private readonly registrationApiUrl: string;
  private readonly platformRequestApiUrl: string;
  private readonly furtherQualificationApiUrl: string;
  private readonly userRegistrationApiUrl: string;
  private readonly cartridgeServiceApiUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {
    this.registrationApiUrl = AppConfigService.appConfiguration.apiUrl['registration'];
    this.platformRequestApiUrl = AppConfigService.appConfiguration.apiUrl['platformRequest'];
    this.furtherQualificationApiUrl = AppConfigService.appConfiguration.apiUrl['furtherQualification'];
    this.userRegistrationApiUrl = AppConfigService.appConfiguration.apiUrl['userRegistration'];
    this.cartridgeServiceApiUrl = AppConfigService.appConfiguration.apiUrl['leistungService'];
  }

  registerNewCustomer(registrationRequest: OrganizationAndUserRegistrationDto): Observable<string> {
    return this.http.post<string>(this.registrationApiUrl, registrationRequest);
  }

  /**
   * Get a information about how many platform requests (pending or in review) are in backoffice. Requester has to be PlatformAdmin
   */
  getPlatformRequestsCount(): Observable<PlatformRequestsDto> {
    return this.http.get<PlatformRequestsDto>(`${this.platformRequestApiUrl}/count`);
  }

  registerExistingOrganization(
    registrationDto: ExistingCustomerRegistrationDto
  ): Observable<ExistingCustomerRegistrationDto> {
    return this.http
      .post<{
        registrationRequest: ExistingCustomerRegistrationDto;
      }>(this.registrationApiUrl + '/existing-customer', registrationDto)
      .pipe(map(context => context.registrationRequest));
  }

  getPlatformRequests(
    index: number,
    size: number,
    query?: string,
    filter?: Record<string, string[]>,
    sort?: SortAttributes
  ): Observable<Page<PlatformRequestData>> {
    const queryParams = buildParameters({
      index: index,
      size: size,
      'sort-field': sort?.sortField,
      'sort-order': sort?.sortOrder,
      search: query,
      ...(filter ?? {}),
    });

    return this.http.get<Page<PlatformRequestData>>(this.platformRequestApiUrl, {
      params: queryParams,
    });
  }

  patchPlatformRequestOrganizationValue(
    platformRequestId: string,
    version: number,
    operations: Operation[]
  ): Observable<PlatformRequestData> {
    return this.http.patch<PlatformRequestData>(
      `${this.platformRequestApiUrl}/${platformRequestId}/organization?version=${version}`,
      operations
    );
  }

  getRegistration(id: string): Observable<OrganizationAndUserRegistrationAuditDto> {
    return this.http.get<OrganizationAndUserRegistrationAuditDto>(`${this.registrationApiUrl}/${id}`);
  }

  getRegistrationDetails(ticket: string): Observable<RegistrationDetailsResponseDto> {
    return this.http.get<RegistrationDetailsResponseDto>(this.userRegistrationApiUrl + `/complete?ticket=${ticket}`);
  }

  completeRegistration(
    completeRegistrationRequestDto: CompleteRegistrationRequestDto,
    ticket: string
  ): Observable<void> {
    return this.http.post<void>(
      this.userRegistrationApiUrl + `/complete?ticket=${ticket}`,
      completeRegistrationRequestDto
    );
  }

  verifyEmail(ticketParam: string): Observable<VerifyEmailResult> {
    return this.http.post<{ isDemoUser: boolean }>(`${this.registrationApiUrl}/verify-email?ticket=${ticketParam}`, {});
  }

  getCartridgeName(identifier: string): Observable<string> {
    return this.http
      .get<{ id: string; name: string }>(`${this.cartridgeServiceApiUrl}/${identifier}?scope=name`)
      .pipe(map(res => res.name));
  }

  setRegistrationStatus(
    registrationId: string,
    backofficeVerificationDto: BackofficeVerificationDto
  ): Observable<void> {
    return this.http.post<void>(
      `${this.registrationApiUrl}/${registrationId}/backoffice-check`,
      backofficeVerificationDto
    );
  }

  getPlatformRequest(id: string): Observable<PlatformRequestData> {
    return this.http.get<PlatformRequestData>(`${this.platformRequestApiUrl}/${id}`);
  }

  validateGlnInPartnerpool(gln: string): Observable<GlnValidationResponseDto> {
    return this.http.get<GlnValidationResponseDto>(`${this.platformRequestApiUrl}/validate/${gln}`);
  }

  checkMarkantIdInPartnerPool({
    name,
    address,
    salesTaxId,
  }: PlatformRequestOrganization): Observable<PartnerPoolDataResult> {
    return this.http.post<PartnerPoolDataResult>(`${this.platformRequestApiUrl}/validate/organization-data`, {
      name,
      address,
      salesTaxId,
    });
  }

  declineRequest(id: string, version: number, comment: string): Observable<PlatformRequestData> {
    return this.completeRequest(id, PlatformRequestStatus.REJECTED, version, comment);
  }

  assignAgentToPlatformRequest(id: string, version: number): Observable<PlatformRequestData> {
    return this.authenticatedUserService.getUser().pipe(
      switchMap(currentUser => {
        const agent: Agent = { id: currentUser.id!, firstname: currentUser.firstname, lastname: currentUser.lastname! };

        return this.http.put<PlatformRequestData>(
          `${this.platformRequestApiUrl}/${id}/agent?version=${version}`,
          agent
        );
      })
    );
  }

  acceptRequest(id: string, version: number, comment: string | undefined): Observable<PlatformRequestData> {
    return this.completeRequest(id, PlatformRequestStatus.APPROVED, version, comment);
  }

  private completeRequest(
    id: string,
    targetStatus: PlatformRequestStatus,
    version: number,
    comment: string | undefined
  ): Observable<PlatformRequestData> {
    return this.http.post<PlatformRequestData>(`${this.platformRequestApiUrl}/${id}/complete?version=${version}`, {
      targetStatus: targetStatus,
      comment: comment,
    });
  }

  getPossibleDuplicates(id: string): Observable<DuplicateResponseDto> {
    return this.http.get<DuplicateResponseDto>(`${this.platformRequestApiUrl}/${id}/duplicates`);
  }

  addAdminToFurtherQualificationRequest(
    id: string,
    user: UserContactDto
  ): Observable<FurtherQualificationLevel3RequestDto> {
    return this.http.patch<FurtherQualificationLevel3RequestDto>(
      `${this.furtherQualificationApiUrl}/${id}/admins`,
      user
    );
  }

  addComment(id: string, comment: CommentRequestDto): Observable<PlatformRequestData> {
    return this.http.post<PlatformRequestData>(`${this.platformRequestApiUrl}/${id}/comment`, comment);
  }

  updatePlatformRequestRequesterData(
    platformRequestId: string,
    userDto: PlatformRequestUserUpdateDto
  ): Observable<PlatformRequestData> {
    return this.http.patch<PlatformRequestData>(`${this.registrationApiUrl}/${platformRequestId}/user`, userDto);
  }
}
